<template>
    <DashboardLayout>
        <div class="d-flex justify-content-center">
            <div class="exec-dashboard">
                <!-- Header -->
                <section class="dash-header d-flex justify-content-between align-items-center">
                    <h1 class="dash-title mb-0">Executive Payment Dashboard</h1>
                    <div class="d-flex gap-2">
                        <!-- Month -->
                        <div class="month-dropdown">
                            <select class="period-btn d-flex align-items-center" v-model="selectedMonth"
                                @change="selectMonth(selectedMonth)">
                                <option value="">Select Month</option>
                                <option v-for="month in monthOptions" :key="month.value" :value="month.value">{{
                                    month.label }}</option>
                            </select>
                        </div>
                        <!-- Year -->
                        <div class="year-dropdown">
                            <select type="button" class="period-btn d-flex align-items-center" v-model="selectedYear"
                                @change="selectYear(selectedYear)">
                                <option value="">Select Year</option>
                                <option v-for="year in yearOptions" :key="year" :value="year">{{ year }}</option>
                            </select>
                        </div>
                    </div>
                </section>

                <!-- Stat cards -->
                <section class="stat-row">
                    <div class="stat-card" v-for="stat in statCards" :key="stat.label">
                        <div class="stat-card-top d-flex justify-content-between align-items-start">
                            <div class="stat-icon" :class="stat.iconClass">
                                <span class="material-symbols-outlined">{{ stat.icon }}</span>
                            </div>
                            <span class="stat-badge" :class="stat.change >= 0 ? 'badge-up' : 'badge-down'">
                                {{ stat.change >= 0 ? '+' : '' }}{{ stat.change }}%
                            </span>
                        </div>
                        <p class="stat-label">{{ stat.label }}</p>
                        <h2 class="stat-value">{{ stat.value }}</h2>
                    </div>
                </section>

                <!-- Charts row -->
                <section class="charts-row">
                    <div class="panel panel-chart">
                        <div class="panel-header">
                            <h3>Payments Over Time</h3>
                            <p class="panel-subtitle">Monthly gross expenditure trends</p>
                            <div class="legend-inline">
                                <span class="legend-dot dot-gross"></span> Gross Payment
                            </div>
                        </div>
                        <div v-if="loadingDashboard" class="loading-state text-center py-5">
                            <div class="spinner-border text-success" role="status" aria-hidden="true"></div>
                            <p class="mt-2 mb-0 text-muted">Loading trend data...</p>
                        </div>
                        <svg v-else class="area-chart" :viewBox="`-25 0 ${chartWidth + 50} ${chartHeight}`"
                            preserveAspectRatio="none">
                            <defs>
                                <linearGradient id="areaFill" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stop-color="#28A745" stop-opacity="0.35" />
                                    <stop offset="100%" stop-color="#28A745" stop-opacity="0" />
                                </linearGradient>
                            </defs>
                            <line v-for="n in 4" :key="'grid' + n" :x1="0" :x2="chartWidth"
                                :y1="(chartInnerHeight / 4) * n" :y2="(chartInnerHeight / 4) * n" class="grid-line" />
                            <path :d="areaPath" fill="url(#areaFill)" />
                            <path :d="linePath" fill="none" stroke="#28A745" stroke-width="2.5" />
                            <g v-for="(point, i) in chartPoints" :key="'pt' + i">
                                <circle :cx="point.x" :cy="point.y" r="4" fill="#28A745" />
                                <text :x="point.x" :y="point.y - 12" class="point-label" text-anchor="middle">
                                    {{ formatCompactCurrency(monthlyTrend[i].amount) }}
                                </text>
                            </g>
                        </svg>
                        <div class="chart-x-axis">
                            <span v-for="m in monthlyTrend" :key="m.label">{{ m.label }}</span>
                        </div>
                    </div>

                    <div class="panel panel-donut">
                        <div class="panel-header">
                            <h3>Deductions Breakdown</h3>
                        </div>
                        <div v-if="loadingDashboard" class="loading-state text-center py-5">
                            <div class="spinner-border text-success" role="status" aria-hidden="true"></div>
                        </div>
                        <template v-else>
                            <div class="donut-wrapper">
                                <div class="donut" :style="{ background: donutGradient }">
                                    <div class="donut-center">
                                        <h2>{{ formatCompactCurrency(deductionsTotal) }}</h2>
                                        <span>TOTAL</span>
                                    </div>
                                </div>
                            </div>
                            <div class="donut-legend">
                                <div class="legend-item" v-for="d in deductionsBreakdown" :key="d.label">
                                    <span class="legend-dot" :style="{ backgroundColor: d.color }"></span>
                                    <div>
                                        <div class="legend-label">{{ d.label }}</div>
                                        <div class="legend-value">{{ (d.percent).toFixed(2) }}% <span class="legend-amount">= {{ formatCompactCurrency(d.amount) }}</span></div>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </div>
                </section>

                <!-- Sector + gross/net row -->
                <section class="mid-row">
                    <div class="panel panel-sector">
                        <h3>Payments by Sector</h3>
                        <div v-if="loadingDashboard" class="loading-state text-center py-5">
                            <div class="spinner-border text-success" role="status" aria-hidden="true"></div>
                        </div>
                        <div v-else class="sector-list">
                            <div class="sector-row" v-for="s in paymentsBySector" :key="s.label">
                                <div class="sector-row-top d-flex justify-content-between">
                                    <span class="sector-label">{{ s.label }}</span>
                                    <span class="sector-amount">{{ formatCompactCurrency(s.amount) }}</span>
                                </div>
                                <div class="progress-track">
                                    <div class="progress-fill" :style="{ width: sectorBarWidth(s.amount) + '%' }"></div>
                                </div>
                            </div>
                            <div v-if="paymentsBySector.length === 0" class="text-center text-muted py-2">
                                No sector payment data to display.
                            </div>
                        </div>
                    </div>

                    <div class="panel panel-gnd">
                        <h3>Gross vs Net vs Deductions</h3>
                        <div v-if="loadingDashboard" class="loading-state text-center py-5">
                            <div class="spinner-border text-success" role="status" aria-hidden="true"></div>
                        </div>
                        <div v-else class="gnd-list">
                            <div class="gnd-row" v-for="row in grossNetDeductions" :key="row.label">
                                <span class="gnd-label">{{ row.label }}</span>
                                <div class="gnd-track">
                                    <div class="gnd-fill" :class="row.variant"
                                        :style="{ width: row.percentOfMax + '%' }">
                                        {{ formatCompactCurrency(row.amount) }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <!-- Top spending MDAs -->
                <section class="panel panel-table">
                    <h3>Top Spending MDAs</h3>
                    <div v-if="loadingDashboard" class="loading-state text-center py-5">
                        <div class="spinner-border text-success" role="status" aria-hidden="true"></div>
                        <p class="mt-2 mb-0 text-muted">Loading MDA expenditure...</p>
                    </div>
                    <div v-else class="table-container border rounded rounded-table">
                        <table class="responsive-table">
                            <thead>
                                <tr>
                                    <th scope="col">MDA</th>
                                    <th scope="col" class="text-end">Total Expenditure</th>
                                    <th scope="col" class="text-center">Percentage</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="mda in topSpendingMdas" :key="mda">
                                    <td class="primary-col text-start">{{ mda.name }}</td>
                                    <td class="secondary-col text-end">{{ formatCompactCurrency(mda.total) }}</td>
                                    <td class="text-center">{{ mda.percentage }}%</td>
                                </tr>
                                <tr v-if="topSpendingMdas.length === 0">
                                    <td colspan="3" class="text-center">No MDA expenditure to display.</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </section>
            </div>
        </div>
    </DashboardLayout>
</template>

<script setup>
import { ref, computed, onMounted, defineOptions } from 'vue'
import axios from 'axios'
import Cookies from 'js-cookie'
import DashboardLayout from '../../layout/DashboardLayout.vue'
import '@/assets/css/table.css'
import '@/assets/css/chart.css'
// import Swal from 'sweetalert2'

defineOptions({
    name: 'ExecutiveDashboardPage',
})

const root_api_url = process.env.VUE_APP_API_ROOT_URL
const loadingDashboard = ref(true)

const currentMonth = new Date().getMonth() + 1
const monthOptions = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
]
const currentYear = new Date().getFullYear()
const yearOptions = [currentYear, currentYear - 1, currentYear - 2]

const selectedMonth = ref(currentMonth)
const selectedYear = ref(currentYear)
// const togglePeriodMenu = () => (periodMenuOpen.value = !periodMenuOpen.value)
// const selectPeriod = (option) => {
//     selectedPeriod.value = option
//     periodMenuOpen.value = false
//     // TODO: refetch dashboard data for the selected period once the API is wired up
// }
const selectMonth = async (option) => {
    selectedMonth.value = option
    await fetchDashboardData() // Refetch dashboard data for the selected month
}
const selectYear = async (option) => {
    selectedYear.value = option
    await fetchDashboardData() // Refetch dashboard data for the selected year
}

/* ------------------------------------------------------------------
   Data — currently mocked to match the design; replace fetchDashboard
   with real endpoint(s) when they're ready.
------------------------------------------------------------------- */
const totalPayments = ref(0)
const totalPVs = ref(0)
const netPayments = ref(0)
const deductionsTotal = ref(0)

const totalPaymentsChange = ref(0)
const totalPVsChange = ref(0)
const netPaymentsChange = ref(0)
const deductionsChange = ref(0)

const monthlyTrend = ref([])
const deductionsBreakdown = ref([])
const paymentsBySector = ref([])
const topSpendingMdas = ref([])

const statCards = computed(() => [
    {
        label: 'Total Payments',
        value: formatCompactCurrency(totalPayments.value),
        change: totalPaymentsChange.value,
        icon: 'payments',
        iconClass: 'icon-green',
    },
    {
        label: 'Total PVs',
        value: totalPVs.value.toLocaleString(),
        change: totalPVsChange.value,
        icon: 'description',
        iconClass: 'icon-green',
    },
    {
        label: 'Net Payments',
        value: formatCompactCurrency(netPayments.value),
        change: netPaymentsChange.value,
        icon: 'account_balance_wallet',
        iconClass: 'icon-green',
    },
    {
        label: 'Deductions',
        value: formatCompactCurrency(deductionsTotal.value),
        change: deductionsChange.value,
        icon: 'compress',
        iconClass: 'icon-red',
    },
])

const grossNetDeductions = computed(() => {
    const rows = [
        { label: 'Gross', amount: totalPayments.value, variant: 'fill-gross' },
        { label: 'Net Payment', amount: netPayments.value, variant: 'fill-net' },
        { label: 'Deductions', amount: deductionsTotal.value, variant: 'fill-deductions' },
    ]
    const max = Math.max(...rows.map((r) => r.amount), 1)
    return rows.map((r) => ({ ...r, percentOfMax: Math.max((r.amount / max) * 100, r.amount > 0 ? 6 : 0) }))
})

const sectorBarWidth = (amount) => {
    const max = Math.max(...paymentsBySector.value.map((s) => s.amount), 1)
    return (amount / max) * 100
}

/* ------------------------------------------------------------------
   Area chart geometry (Payments Over Time)
------------------------------------------------------------------- */
const chartWidth = 700
const chartHeight = 340
const chartInnerHeight = 260
const chartTopPadding = 40

const chartPoints = computed(() => {
    if (!monthlyTrend.value.length) return []
    const amounts = monthlyTrend.value.map((m) => m.amount)
    const max = Math.max(...amounts, 1)
    const min = 0
    const stepX = chartWidth / (monthlyTrend.value.length - 1 || 1)
    return monthlyTrend.value.map((m, i) => {
        const ratio = (m.amount - min) / (max - min || 1)
        return {
            x: i * stepX,
            y: chartTopPadding + (chartInnerHeight - ratio * chartInnerHeight),
        }
    })
})

const linePath = computed(() => {
    const pts = chartPoints.value
    if (!pts.length) return ''
    return pts.reduce((d, p, i) => d + (i === 0 ? `M${p.x},${p.y}` : ` L${p.x},${p.y}`), '')
})

const areaPath = computed(() => {
    const pts = chartPoints.value
    if (!pts.length) return ''
    const base = chartTopPadding + chartInnerHeight
    const first = pts[0]
    const last = pts[pts.length - 1]
    return `${linePath.value} L${last.x},${base} L${first.x},${base} Z`
})

/* ------------------------------------------------------------------
   Donut chart (Deductions Breakdown) — built with conic-gradient
------------------------------------------------------------------- */
const donutGradient = computed(() => {
    console.log("Calculating donut gradient for deductions breakdown:", deductionsBreakdown.value)
    if (!deductionsBreakdown.value.length) return '#e9ecef'
    let cumulative = 0
    const stops = deductionsBreakdown.value.map((d) => {
        const start = cumulative
        console.log("Deduction:", d.label, "Percent:", d.percent, "Start:", start, "Cumulative:", cumulative)
        cumulative += d.percent
        return `${d.color} ${start}% ${cumulative}%`
    })
    console.log("Stops:", stops)
    return `conic-gradient(${stops.join(', ')})`
})


/* ------------------------------------------------------------------
   Formatting helpers
------------------------------------------------------------------- */
function formatCompactCurrency(val) {
    if (val === '' || val === null || val === undefined) return '₦0'
    const num = Number(val)
    const abs = Math.abs(num)
    let display
    if (abs >= 1_000_000_000) display = (num / 1_000_000_000).toFixed(1) + 'B'
    else if (abs >= 1_000_000) display = (num / 1_000_000).toFixed(1) + 'M'
    else if (abs >= 1_000) display = (num / 1_000).toFixed(1) + 'K'
    else display = num.toFixed(0)
    return '₦' + display
}

/* ------------------------------------------------------------------
   Color Palette for deduction breakdown
------------------------------------------------------------------- */
const deductionPalette = [
    '#4361EE',
    '#3A86FF',
    '#8338EC',
    '#FF006E',
    '#FB5607',
    '#FFBE0B',
    '#06D6A0',
    '#118AB2',
    '#EF476F',
    '#7209B7',
]

const getDeductionColor = (index) => {
    return deductionPalette[index % deductionPalette.length]
}

// DASHBOARD DATA
const fetchDashboardData = async () => {
    loadingDashboard.value = true
    const token = Cookies.get('auth_token')
    const year = selectedYear.value
    const month = selectedMonth.value
    try {
        const { data } = await axios.get(`${root_api_url}/payment/dashboard/executive`, {
            params: {
                year,
                month
            },
            headers: { Authorization: `Bearer ${token}` }
        })
        const { deductionBreakdown, sectors, summary, topMdas, trends } = data
        console.log("Dashboard data:", data)

        // Deduction Breakdown
        deductionsBreakdown.value = deductionBreakdown.map((d, index) => ({
            label: d.deductionType,
            amount: d.amount,
            percent: d.percentage,
            color: getDeductionColor(index),
        })).sort((a, b) => b.percent - a.percent) // Sort by percentage descending

        console.log("Deduction Value:", deductionsBreakdown.value)
        console.log("Deduction Breakdown First Data:", deductionsBreakdown.value[0])

        // Sectors
        paymentsBySector.value = sectors.map(s => ({ label: s.sector, amount: s.amount }))

        // Summary
        summary.value = data.summary
        totalPayments.value = summary.totalPayments
        totalPVs.value = summary.totalPVs
        netPayments.value = summary.netPayments
        deductionsTotal.value = summary.totalDeductions

        // Trends
        monthlyTrend.value = trends.map(t => ({ label: t.month, amount: t.amount }))

        // Top MDAs
        topSpendingMdas.value = topMdas.map(m => ({ name: m.mdaName, percentage: (m.percentage).toFixed(2), total: m.total }))

        // Change
        const currentTotalPayments = summary.totalPayments
        const previousTotalPayments = trends[trends.length - 2].amount
        totalPaymentsChange.value = ((currentTotalPayments - previousTotalPayments) / previousTotalPayments * 100).toFixed(0)

    } catch (error) {
        console.error('Error fetching dashboard data:', error)
    } finally {
        loadingDashboard.value = false
    }
}

onMounted(async () => {
    await fetchDashboardData()
    document.title = 'Executive Dashboard'
})

</script>

<style scoped>
.exec-dashboard {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    width: calc(100% - 3rem);
    margin: 0 auto;
}

/* Header */
.dash-header {
    margin-bottom: 0.5rem;
}

.dash-title {
    font-size: 2rem;
    font-weight: 700;
    color: #1E7A34;
}

.period-dropdown {
    position: relative;
}

.period-btn {
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    padding: 0.5rem 1rem;
    font-weight: 500;
    color: #333;
}

.period-menu {
    position: absolute;
    right: 0;
    top: calc(100% + 4px);
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    list-style: none;
    padding: 0.25rem 0;
    margin: 0;
    min-width: 140px;
    z-index: 10;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.period-menu li {
    padding: 0.5rem 1rem;
    cursor: pointer;
}

.period-menu li:hover {
    background: #f4f9f5;
}

/* Stat cards */
.stat-row {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 1.25rem;
}

@media (max-width: 992px) {
    .stat-row {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 576px) {
    .stat-row {
        grid-template-columns: 1fr;
    }
}

.stat-icon {
    width: 42px;
    height: 42px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.stat-icon.icon-green {
    background: #E6F4EA;
    color: #28A745;
}

.stat-icon.icon-red {
    background: #FBE7E9;
    color: #DC3545;
}

.stat-badge {
    font-size: 0.8rem;
    font-weight: 600;
    padding: 0.2rem 0.6rem;
    border-radius: 999px;
}

.badge-up {
    background: #E6F4EA;
    color: #1E7A34;
}

.badge-down {
    background: #FBE7E9;
    color: #DC3545;
}

/* Panels */
.panel h3 {
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 1.25rem;
}

.panel-subtitle {
    color: #6c757d;
    font-size: 0.85rem;
    margin: -1rem 0 0;
}

.charts-row {
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 1.25rem;
}

@media (max-width: 992px) {
    .charts-row {
        grid-template-columns: 1fr;
    }
}

.panel-header {
    position: relative;
    margin-bottom: 1.5rem;
}

.legend-inline {
    position: absolute;
    top: 0;
    right: 0;
    font-size: 0.8rem;
    color: #6c757d;
    display: flex;
    align-items: center;
}

.legend-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    display: inline-block;
    margin-right: 0.4rem;
}

.dot-gross {
    background: #28A745;
}

.area-chart {
    width: 100%;
    height: 260px;
    display: block;
}

.grid-line {
    stroke: #eef1ee;
    stroke-width: 1;
}

.point-label {
    font-size: 13px;
    fill: #1a1a1a;
    font-weight: 600;
}

.chart-x-axis {
    display: flex;
    justify-content: space-between;
    color: #6c757d;
    font-size: 0.8rem;
    letter-spacing: 0.03em;
    padding: 0 0.25rem;
}

/* Donut */
.donut-wrapper {
    display: flex;
    justify-content: center;
    margin-bottom: 1.5rem;
}

.donut {
    width: 190px;
    height: 190px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
}


.donut-center {
    position: relative;
    z-index: 1;
    text-align: center;
}

.donut-center h2 {
    font-size: 1.3rem;
    font-weight: 700;
    margin: 0;
}

.donut-center span {
    font-size: 0.7rem;
    color: #6c757d;
    letter-spacing: 0.05em;
}

.donut-legend {
    display: grid;
    grid-template-columns: 1fr 1fr;
    row-gap: 1rem;
    column-gap: 1rem;
}

.legend-item {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
}

.legend-item .legend-dot {
    margin-top: 0.35rem;
}

.legend-label {
    font-size: 0.8rem;
    color: #6c757d;
}

.legend-value {
    font-weight: 700;
    font-size: 0.95rem;
}

.legend-amount {
    font-weight: 100;
    font-size: 0.95rem;
}

/* Sector + gross/net row */
.mid-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.25rem;
}

@media (max-width: 992px) {
    .mid-row {
        grid-template-columns: 1fr;
    }
}

.sector-row {
    margin-bottom: 1.1rem;
}

.sector-row:last-child {
    margin-bottom: 0;
}

.sector-label {
    font-size: 0.85rem;
    font-weight: 600;
    color: #333;
}

.sector-amount {
    font-size: 0.85rem;
    font-weight: 600;
    color: #333;
}

.progress-track {
    height: 8px;
    background: #eef1ee;
    border-radius: 999px;
    margin-top: 0.4rem;
    overflow: hidden;
}

.progress-fill {
    height: 100%;
    background: #28A745;
    border-radius: 999px;
}

.gnd-row {
    margin-bottom: 1.25rem;
}

.gnd-row:last-child {
    margin-bottom: 0;
}

.gnd-label {
    font-size: 0.8rem;
    color: #6c757d;
    letter-spacing: 0.03em;
    display: block;
    margin-bottom: 0.4rem;
}

.fill-gross {
    background: #1E7A34;
}

.fill-net {
    background: #28A745;
}

.fill-deductions {
    background: #DC3545;
}

/* Table */
.panel-table .table-container {
    margin-top: 0.5rem;
}

.mda-status {
    font-weight: 600;
}

.status-active {
    color: #28A745;
}
</style>